import React, { useState, useEffect, useMemo } from 'react';
import { Amplify, Auth, API, Storage, Cache } from 'aws-amplify';
import { Flex, View, Button, Text, Divider, TextField, ScrollView } from '@aws-amplify/ui-react';
import { withAuthenticator } from '@aws-amplify/ui-react';
import awsconfig from '../aws-exports';
import GeneralFooter from '../ui-components/GeneralFooter';
import DialogModal from '../components/dialog-modal.tsx';
import { useNavigate } from 'react-router';

import { getUser } from '../graphql/queries';

Amplify.configure(awsconfig);

function BuyerCompanyDetailsContainer() {
    const navigate = useNavigate();
    const [tableData, setTableData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogModalOpen, setDialogModalOpen] = useState(false);
    const [dialogModalMessage, setDialogModalMessage] = useState('');
    const [totalBuyers, setTotalBuyers] = useState(0);
    
    const handleCloseModal = () => {
        setDialogModalOpen(false);
    };

    useEffect(() => {
        //fetchBuyerCompanyDetails();
        downloadBuyerCompanyDetails();
    }, []);

    async function downloadBuyerCompanyDetails() {
        setLoading(true);
        try {
            const market = Cache.getItem('marketCode');
            let uniqueBuyersURL = `unique_buyers/${market}_unique_buyers.csv`;
            let uniqueBuyersData = [];
    
            try {
                const uniqueBuyersResult = await Storage.vault.get(uniqueBuyersURL, { download: true });
                const csvText = await new Response(uniqueBuyersResult.Body).text();
                const rows = csvText.split('\n').slice(1); // Skip header
    
                // Create a Map to store unique buyers
                const uniqueBuyersMap = new Map();
    
                rows
                    .filter(row => row.trim() !== '')
                    .forEach(row => {
                        // Function to parse CSV line properly
                        const parseCSVLine = (line) => {
                            const results = [];
                            let field = '';
                            let inQuotes = false;
                            
                            for (let i = 0; i < line.length; i++) {
                                const char = line[i];
                                
                                if (char === '"') {
                                    inQuotes = !inQuotes;
                                    continue;
                                }
                                
                                if (char === ',' && !inQuotes) {
                                    results.push(field.trim());
                                    field = '';
                                    continue;
                                }
                                
                                field += char;
                            }
                            
                            results.push(field.trim());
                            return results;
                        };
    
                        const [companyName, city, stateRegion, postalCode, dollarSpent] = parseCSVLine(row);

                        // Skip entries with empty city, state/region, or postal code
                        if (!city && !stateRegion && !postalCode) {
                            return;
                        }
                        
                        // Clean up the dollar spent value (remove $ and any other non-numeric characters except .)
                        const cleanDollarSpent = dollarSpent ? dollarSpent.replace(/[^\d.]/g, '') : '0';
                        
                        // Create normalized key
                        const key = [
                            companyName.trim().toLowerCase(),
                            city.trim().toLowerCase(),
                            stateRegion.trim().toLowerCase(),
                            postalCode.trim().toLowerCase()
                        ].join('-');
    
                        const buyer = {
                            companyName: companyName.trim(),
                            shippingAddress: {
                                city: city.trim(),
                                stateOrRegion: stateRegion.trim(),
                                postalCode: postalCode.trim()
                            },
                            dollarSpent: parseFloat(cleanDollarSpent).toFixed(2)
                        };
    
                        if (!uniqueBuyersMap.has(key)) {
                            uniqueBuyersMap.set(key, buyer);
                        } else {
                            const existingBuyer = uniqueBuyersMap.get(key);
                            const newTotal = (parseFloat(existingBuyer.dollarSpent) + parseFloat(cleanDollarSpent)).toFixed(2);
                            existingBuyer.dollarSpent = newTotal;
                            uniqueBuyersMap.set(key, existingBuyer);
                        }
                    });
    
                // Convert Map to array
                uniqueBuyersData = Array.from(uniqueBuyersMap.values());
    
            } catch (error) {
                console.log("Unique buyers data not found or error occurred:", error);
                uniqueBuyersData = [];
            }
    
            // Set the table data
            setTableData(uniqueBuyersData);
            setTotalBuyers(uniqueBuyersData.length);
    
        } catch (exception) {
            console.log(exception);
            setDialogModalMessage('Failed to download buyer company details');
            setDialogModalOpen(true);
        } finally {
            setLoading(false);
        }
    }
    
    // Buyer Company Details Table Component
    const BuyerCompanyDetailsTable = ({ data }) => {
        const [currentPage, setCurrentPage] = useState(1);
        const [searchTerm, setSearchTerm] = useState("");
        const [sortField, setSortField] = useState(null);
        const [sortDirection, setSortDirection] = useState('asc');
        const itemsPerPage = 10;


         // Ensure data is an array
        const safeData = Array.isArray(data) ? data : [];
        const sortData = (data, field, direction) => {
            return [...data].sort((a, b) => {
                let valueA = field === 'dollarSpent' 
                    ? parseFloat(a[field]) 
                    : field.includes('.') 
                        ? a.shippingAddress[field.split('.')[1]].toLowerCase()
                        : a[field].toLowerCase();
                let valueB = field === 'dollarSpent'
                    ? parseFloat(b[field])
                    : field.includes('.')
                        ? b.shippingAddress[field.split('.')[1]].toLowerCase()
                        : b[field].toLowerCase();
                
                if (valueA < valueB) return direction === 'asc' ? -1 : 1;
                if (valueA > valueB) return direction === 'asc' ? 1 : -1;
                return 0;
            });
        };
        const filteredData = useMemo(() => {
            let filtered = safeData.filter(item => 
                (item.companyName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                (item.shippingAddress?.city || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                (item.shippingAddress?.stateOrRegion || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                (item.shippingAddress?.postalCode || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                (item.dollarSpent || '').toString().includes(searchTerm.toLowerCase())
            );
        
            if (sortField) {
                filtered = sortData(filtered, sortField, sortDirection);
            }
        
            return filtered;
        }, [safeData, searchTerm, sortField, sortDirection]);
        const handleSort = (field) => {
            if (sortField === field) {
                setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
            } else {
                setSortField(field);
                setSortDirection('asc');
            }
        };
        
        // Add this style object for the sort indicators
        const getSortIndicator = (field) => {
            if (sortField !== field) return '↕';
            return sortDirection === 'asc' ? '↑' : '↓';
        };
        


        // Memoized pagination logic
        const paginatedData = useMemo(() => {
            const indexOfLastItem = currentPage * itemsPerPage;
            const indexOfFirstItem = indexOfLastItem - itemsPerPage;
            return filteredData.slice(indexOfFirstItem, indexOfLastItem);
        }, [filteredData, currentPage]);

        // Calculate total pages
        const totalPages = Math.ceil(filteredData.length / itemsPerPage);

        // Pagination button rendering
        const renderPaginationButtons = () => {
            const buttons = [];
            const maxVisibleButtons = 5;
            
            // Calculate start and end pages
            let startPage = Math.max(1, currentPage - Math.floor(maxVisibleButtons / 2));
            let endPage = Math.min(totalPages, startPage + maxVisibleButtons - 1);

            // Adjust if we're near the end
            if (endPage - startPage + 1 < maxVisibleButtons) {
                startPage = Math.max(1, endPage - maxVisibleButtons + 1);
            }

            // First page button
            if (startPage > 1) {
                buttons.push(
                    <Button 
                        key="first" 
                        onClick={() => setCurrentPage(1)}
                        variation="outline"
                        size="small"
                    >
                        First
                    </Button>
                );
            }

            // Page number buttons
            for (let i = startPage; i <= endPage; i++) {
                buttons.push(
                    <Button 
                        key={i} 
                        onClick={() => setCurrentPage(i)}
                        variation={currentPage === i ? 'primary' : 'outline'}
                        size="small"
                    >
                        {i}
                    </Button>
                );
            }

            // Last page button
            if (endPage < totalPages) {
                buttons.push(
                    <Button 
                        key="last" 
                        onClick={() => setCurrentPage(totalPages)}
                        variation="outline"
                        size="small"
                    >
                        Last
                    </Button>
                );
            }

            return buttons;
        };

        return (
            <Flex direction="column" width="100%">
            <Flex direction="column" marginBottom="1rem">
                <Text>Total Unique Buyers (last 2 years): {totalBuyers}</Text>
                <TextField
                placeholder="Search..."
                onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1);
                }}
                width="300px"
                marginTop="0.5rem"
                />
            </Flex>
            
            <ScrollView width="100%" maxHeight="500px">
                <table style={{ 
                width: '100%', 
                borderCollapse: 'collapse', 
                tableLayout: 'fixed' 
                }}>
                <thead style={{ position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 1 }}>
                    <tr style={{ backgroundColor: '#f2f2f2' }}>
                    <th style={{ 
                        border: '1px solid #ddd', 
                        padding: '8px', 
                        textAlign: 'left', 
                        width: '40%',
                        cursor: 'pointer'
                    }} onClick={() => {
                        handleSort('companyName');
                        setCurrentPage(1);
                    }}>
                        Buyer Name {sortField === 'companyName' && getSortIndicator('companyName')}
                    </th>
                    <th style={{ 
                        border: '1px solid #ddd', 
                        padding: '8px', 
                        textAlign: 'left', 
                        width: '20%',
                        cursor: 'pointer'
                    }} onClick={() => {
                        handleSort('shippingAddress.city');
                        setCurrentPage(1);
                    }}>
                        City {sortField === 'shippingAddress.city' && getSortIndicator('shippingAddress.city')}
                    </th>
                    <th style={{ 
                        border: '1px solid #ddd', 
                        padding: '8px', 
                        textAlign: 'left', 
                        width: '20%',
                        cursor: 'pointer'
                    }} onClick={() => {
                        handleSort('shippingAddress.stateOrRegion');
                        setCurrentPage(1);
                    }}>
                        State/Region {sortField === 'shippingAddress.stateOrRegion' && getSortIndicator('shippingAddress.stateOrRegion')}
                    </th>
                    <th style={{ 
                        border: '1px solid #ddd', 
                        padding: '8px', 
                        textAlign: 'left', 
                        width: '20%',
                        cursor: 'pointer'
                    }} onClick={() => {
                        handleSort('shippingAddress.postalCode');
                        setCurrentPage(1);
                    }}>
                        Postal Code {sortField === 'shippingAddress.postalCode' && getSortIndicator('shippingAddress.postalCode')}
                    </th>
                    <th style={{ 
                        border: '1px solid #ddd', 
                        padding: '8px', 
                        textAlign: 'left', 
                        width: '20%',
                        cursor: 'pointer'
                    }} onClick={() => {
                        handleSort('dollarSpent');
                        setCurrentPage(1);
                    }}>
                        Amount Spent {sortField === 'dollarSpent' && getSortIndicator('dollarSpent')}
                    </th>
                    </tr>
                </thead>
                <tbody>
                    {paginatedData.map((item, index) => (
                    <tr key={index} style={{ backgroundColor: index % 2 === 0 ? '#f9f9f9' : 'white' }}>
                        <td style={{ 
                        border: '1px solid #ddd', 
                        padding: '8px', 
                        whiteSpace: 'nowrap', 
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis' 
                        }}>
                        {item.companyName || 'N/A'}
                        </td>
                        <td style={{ 
                        border: '1px solid #ddd', 
                        padding: '8px', 
                        whiteSpace: 'nowrap', 
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis' 
                        }}>
                        {item.shippingAddress.city}
                        </td>
                        <td style={{ 
                        border: '1px solid #ddd', 
                        padding: '8px', 
                        whiteSpace: 'nowrap', 
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis' 
                        }}>
                        {item.shippingAddress.stateOrRegion}
                        </td>
                        <td style={{ 
                        border: '1px solid #ddd', 
                        padding: '8px', 
                        whiteSpace: 'nowrap', 
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis' 
                        }}>
                        {item.shippingAddress.postalCode}
                        </td>
                        <td style={{ 
                        border: '1px solid #ddd', 
                        padding: '8px', 
                        whiteSpace: 'nowrap', 
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis' 
                        }}>
                        ${parseFloat(item.dollarSpent).toFixed(2)}
                        </td>
                    </tr>
                    ))}
                </tbody>
                </table>
            </ScrollView>
            
            <Flex justifyContent="center" marginTop="20px" gap="10px" wrap="wrap">
                {/* First page button */}
                {currentPage > 1 && (
                <Button 
                    onClick={() => setCurrentPage(1)}
                    variation="outline"
                    size="small"
                >
                    First
                </Button>
                )}
            
                {/* Previous page button */}
                {currentPage > 1 && (
                <Button 
                    onClick={() => setCurrentPage(currentPage - 1)}
                    variation="outline"
                    size="small"
                >
                    Previous
                </Button>
                )}
            
                {/* Page numbers */}
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                const pageNum = Math.max(1, currentPage - 2) + i;
                if (pageNum > 0 && pageNum <= totalPages) {
                    return (
                    <Button 
                        key={pageNum}
                        onClick={() => setCurrentPage(pageNum)}
                        variation={currentPage === pageNum ? 'primary' : 'outline'}
                        size="small"
                    >
                        {pageNum}
                    </Button>
                    );
                }
                return null;
                })}
            
                {/* Next page button */}
                {currentPage < totalPages && (
                <Button 
                    onClick={() => setCurrentPage(currentPage + 1)}
                    variation="outline"
                    size="small"
                >
                    Next
                </Button>
                )}
            
                {/* Last page button */}
                {currentPage < totalPages && (
                <Button 
                    onClick={() => setCurrentPage(totalPages)}
                    variation="outline"
                    size="small"
                >
                    Last
                </Button>
                )}
            </Flex>
            </Flex>
        );
    };

    return (
        <div style={{ width: "100%" }}>
            <Flex paddingTop={'20px'} paddingLeft={'30px'} paddingRight={'30px'} overflow={'auto'}>
                <Flex gap="32px" direction="column" width="100%" justifyContent="flex-start" alignItems="flex-start" overflow="hidden" position="relative">
                    <Flex gap="8px" direction="column" justifyContent="flex-start" alignItems="flex-start">
                        <Text fontFamily="Inter" fontSize="32px" fontWeight="500" color="rgba(13,26,38,1)" lineHeight="40px" textAlign="left">
                            Buyer Company Details
                        </Text>
                    </Flex>
                    {loading ? (
                        <Flex width="100%" height="300px" justifyContent="center" alignItems="center">
                            <Text fontSize="20px" fontWeight="400" color="rgba(13,26,38,1)">
                                Loading buyer company details. Please wait...
                            </Text>
                        </Flex>
                    ) : (
                        <BuyerCompanyDetailsTable data={tableData} />
                    )}
                </Flex>
            </Flex>
            <GeneralFooter />
            <DialogModal open={dialogModalOpen} message={dialogModalMessage} onClose={handleCloseModal} />
        </div>
    );
}

export default withAuthenticator(BuyerCompanyDetailsContainer);